import { zipcodeValidator } from "../../lib/Validator";
import { SUPPORTED_LANGUAGES } from "../../lib/Languages";

const preferredLanguageFilter = {
  label: "Preferred Language",
  description: "Only send to patients whose preferred language matches",
  id: "settings.filter.preferred_language_filter",
  type: "toggle_select",
  condition: ({ medium }) => medium !== "Phone",
  title: "Language",
  options: SUPPORTED_LANGUAGES,
};

const campaignTypes = [
  {
    name: "Reminder",
    id: "reminder",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Enable AI",
        type: "checkbox",
        hint: "Must have Autopilot enabled (Phones > Routing Plans)",
        condition: ({ medium }) => medium === "Phone",
        id: "settings.ai_enabled",
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Same-day Reminder",
        description: "",
        id: "settings.sending_date.same_day.enabled",
        type: "checkbox",
      },
      {
        label: "Number of Days Before Sending the Reminder",
        id: "settings.sending_date.previous_day.days_before",
        type: "rangeInt",
        condition: ({ template }) =>
          !template?.settings?.sending_date?.same_day?.enabled,
        required: true,
        title: "Days",
        range: [[1, 30]],
      },
      {
        label: "Time to send",
        id: "settings.sending_date.previous_day.send_time",
        type: "select",
        required: true,
        title: "Time",
        condition: ({ template }) =>
          !template?.settings?.sending_date?.same_day?.enabled,
        options: [
          6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13,
          13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5,
          20, 20.5, 21,
        ].map((v) => ({
          value: v,
          name: (v >= 12 && v < 13
              ? `${v} pm`
              : v >= 13
                ? `${v - 12} pm`
                : `${v} am`
          ).replace(".5", ":30"),
        })),
      },
      {
        label: "Hours Before Appointment",
        id: "settings.sending_date.same_day.hours_before",
        type: "rangeInt",
        condition: ({ template }) =>
          template?.settings?.sending_date?.same_day?.enabled,
        required: true,
        title: "Hours",
        range: [[1, 12]],
      },
      {
        label: "Contact even if the appointment has been confirmed",
        description: "",
        type: "checkbox",
        id: "settings.send_to_confirmed",
      },
      {
        id: "settings.phone_type.home",
        type: "checkbox",
        description: "",
        label: "Contact home phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.phone_type.cell",
        type: "checkbox",
        description: "",
        label: "Contact cell phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.phone_type.work",
        type: "checkbox",
        description: "",
        label: "Contact work phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.callback_times_if_no_pickup",
        type: "rangeInt",
        description: "",
        label: "Number of times to call back if call is not picked up",
        condition: ({ medium }) => medium === "Phone",
        title: "Times",
        range: [[1, 5]]
      },
      {
        type: "checkbox",
        id: "settings.overlap_protection",
        label:
          "Do not send any reminders during the day in case the patient was already notified by any reminder type",
        description: "",
      },
      {
        type: "checkbox",
        id: "settings.opt_out_reduction",
        label:
          "Add the opt-out message only at the end of the first text message that was sent",
        condition: ({ medium }) => medium === "SMS",
      },
      {
        label: "Don't send message before",
        id: "settings.sending_date.same_day.send_after_time",
        type: "select",
        required: true,
        title: "hours",
        condition: ({ template }) => {
          return template?.settings?.sending_date?.same_day?.enabled;
        },
        options: [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11].map(
          (v) => ({
            value: v,
            name: (v >= 12 && v < 13
                ? `${v} pm`
                : v >= 13
                  ? `${v - 12} pm`
                  : `${v} am`
            ).replace(".5", ":30"),
          })
        ),
      },
      {
        label: "",
        id: "settings.text_reduction",
        condition: ({ medium }) => medium === "SMS",
        type: "radio",
        options: [
          { name: "Send for all appointments", value: "" },
          {
            name: "Send only for the first appointment of each patient in the group",
            value: "patient",
          },
          {
            name: "Send only for the first appointment of the group",
            value: "group",
          },
        ],
      },
      {
        type: "checkbox",
        id: "settings.call_reduction",
        label: "Call only for the first appointment",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        label: "Messages that are to be sent on weekends",
        type: "radio",
        id: "settings.filter.move_weekend",
        options: [
          { name: "Send normally", value: "0" },
          { name: "Send previous Friday", value: "-1" },
        ],
        condition: ({ template }) => !template?.settings?.sending_date?.same_day?.enabled
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      {
        label: "Oral Health Score",
        id: "settings.filter.oral_health_score_range",
        type: "rangeInt",
        range: [
          [20, 100],
          [20, 100],
        ],
      },
      preferredLanguageFilter,
    ],
  },
  {
    name: "Manual",
    id: "manual",
    filters: [
      {
        label: "Status",
        id: "status",
        type: "select",
        options: [
          {
            value: "draft",
            name: "Draft",
          },
          {
            value: "scheduled",
            name: "Active",
          },
        ],
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Schedule Campaign",
        type: "datetime",
        id: "settings.sending_date.fixed_date",
        required: true,
      },
      {
        id: "settings.phone_type.home",
        type: "checkbox",
        description: "",
        label: "Contact home phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.phone_type.cell",
        type: "checkbox",
        description: "",
        label: "Contact cell phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.phone_type.work",
        type: "checkbox",
        description: "",
        label: "Contact work phone",
        condition: ({ medium }) => medium === "Phone",
      },
      {
        id: "settings.callback_times_if_no_pickup",
        type: "rangeInt",
        description: "",
        label: "Number of times to call back if call is not picked up",
        condition: ({ medium }) => medium === "Phone",
        title: "Times",
        range: [[1, 5]]
      },
      {
        id: "settings.filter.outstanding_treatment_categories",
        label: "Outstanding treatment categories",
        type: "toggle_array",
        options: [
          { name: "Diagnostic (D0100-D0999)", value: { from: "D0100", to: "D0999" } },
          { name: "Preventive (D1000-D1999)", value: { from: "D1000", to: "D1999" } },
          { name: "Restorative (D2000-D2999)", value: { from: "D2000", to: "D2999" } },
          { name: "Endodontics (D3000-D3999)", value: { from: "D3000", to: "D3999" } },
          { name: "Periodontics (D4000-D4999)", value: { from: "D4000", to: "D4999" } },
          { name: "Prosthodontics (D5000-D5899)", value: { from: "D5000", to: "D5899" } },
          { name: "Implant Services (D6000-D6199)", value: { from: "D6000", to: "D6199" } },
          { name: "Maxillofacial Prosthetics (D5900-D5999)", value: { from: "D5900", to: "D5999" } },
          { name: "Oral Surgery (D7000-D7999)", value: { from: "D7000", to: "D7999" } },
          { name: "Orthodontics (D8000-D8999)", value: { from: "D8000", to: "D8999" } },
          { name: "Adjunctive General Services (D9000-D9999)", value: { from: "D9000", to: "D9999" } },
          { name: "All Categories (D0100-D9999)", value: { from: "D0100", to: "D9999" } },
          { name: "Hygiene Recare", value: { from: "HYG000", to: "HYG999" } }
        ],
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      {
        label: "Patients",
        id: "settings.filter.patient_ids",
      },
      {
        label: "Oral Health Score",
        id: "settings.filter.oral_health_score_range",
        type: "rangeInt",
        range: [
          [20, 100],
          [20, 100],
        ],
      },
      {
        label: "Age",
        id: "settings.filter.age_range",
        type: "rangeInt",
        range: [
          [1, 120],
          [1, 120],
        ],
      },
      {
        label: "Last Visit",
        id: "settings.filter.last_visit_range",
        type: "rangeDate",
      },
      {
        label: "Last Visit",
        description: "Exclude patients with future appointments",
        id: "settings.filter.exclude_patients_with_future_appointments",
        type: "checkbox",
      },
      {
        label: "Zipcode",
        id: "settings.filter.zip_codes",
        type: "toggle_array",
        pattern: zipcodeValidator,
      },
      {
        label: "Exclude duplicates",
        description:
          "Relates to patients sharing the same email/phone, not reflected in Patients counter",
        id: "settings.filter.exclude_duplicate_patients",
        type: "checkbox",
      },
      preferredLanguageFilter,
    ],
  },
  {
    name: "Welcome",
    id: "welcome",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        id: "settings.filter.exclude_if_has_sameday_appointments",
        type: "checkbox",
        label: "Exclude patients scheduled same day",
      },
      {
        id: "settings.filter.send_right_away",
        type: "checkbox",
        label: "Send right away",
      },
      {
        id: "settings.sending_date.fixed_time",
        type: "select",
        label: "Send at a specific time:",
        condition: ({ template }) => !template.settings.filter.send_right_away,
        title: "Time",
        options: [
          7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14,
          14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5,
          21,
        ].map((v) => ({
          value: v,
          name: (v >= 12 && v < 13
              ? `${v} pm`
              : v >= 13
                ? `${v - 12} pm`
                : `${v} am`
          ).replace(".5", ":30"),
        })),
        required: true,
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      preferredLanguageFilter,
    ],
  },
  {
    name: "Birthday",
    id: "birthday",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Send it the same day",
        description: "",
        id: "settings.sending_date.same_day.enabled",
        type: "checkbox",
      },
      {
        label: "Send it a number of days before the birthday",
        id: "settings.sending_date.previous_day.days_before",
        type: "rangeInt",
        condition: ({ template }) =>
          !template?.settings?.sending_date?.same_day?.enabled,
        required: true,
        title: "Days",
        range: [[1, 3]],
      },
      {
        label: "Time to send",
        id: "settings.sending_date.previous_day.send_time",
        type: "select",
        required: true,
        title: "Time",
        condition: ({ template }) =>
          !template?.settings?.sending_date?.same_day?.enabled,
        options: [
          6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13,
          13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5,
          20, 20.5, 21,
        ].map((v) => ({
          value: v,
          name: (v >= 12 && v < 13
              ? `${v} pm`
              : v >= 13
                ? `${v - 12} pm`
                : `${v} am`
          ).replace(".5", ":30"),
        })),
      },
      {
        id: "settings.sending_date.same_day.send_after_time",
        type: "select",
        label: "Time to send",
        condition: ({ template }) => template?.settings?.sending_date?.same_day?.enabled,
        title: "Time",
        options: [
          7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14,
          14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20, 20.5,
          21,
        ].map((v) => ({
          value: v,
          name: (v >= 12 && v < 13
              ? `${v} pm`
              : v >= 13
                ? `${v - 12} pm`
                : `${v} am`
          ).replace(".5", ":30"),
        })),
        required: true
      },
      {
        id: "settings.filter.exclude_if_has_sameday_appointments",
        type: "checkbox",
        label: "Exclude patients scheduled same day",
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      preferredLanguageFilter,
    ]
  },
  {
    name: "Review",
    id: "review",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Hours After The Appointment",
        id: "settings.sending_date.hours_since_the_event",
        type: "select",
        required: true,
        title: "Hours",
        options: [
          2, 5, 10, 24, 48
        ].map((v) => ({
          value: v,
          name: v
        })),
      },
      {
        label: "Minimum Rating To Post External Review",
        id: "settings.nps",
        type: "rangeInt",
        required: true,
        title: "Score",
        range: [[1, 10]],
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      {
        label: "Oral Health Score",
        id: "settings.filter.oral_health_score_range",
        type: "rangeInt",
        range: [
          [20, 100],
          [20, 100],
        ],
      },
      preferredLanguageFilter,
    ]
  },
  {
    name: "Save the Date",
    id: "confirmation",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Hours After The Appointment Is Created",
        id: "settings.sending_date.hours_since_the_event",
        type: "select",
        required: true,
        title: "Hours",
        options: [
          1, 2, 3, 4, 5, 6, 8, 10, 12, 24
        ].map((v) => ({
          value: v,
          name: v
        })),
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      preferredLanguageFilter,
    ],
  },
  {
    name: "Reactivation",
    id: "reactivation",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Email Subject",
        type: "text",
        condition: ({ medium }) => medium === "Email",
        id: "subject",
        required: true,
      },
      {
        label: "Recurrence Limit",
        id: "settings.reactivation.recurrence",
        type: "rangeInt",
        required: true,
        title: "recurrence",
        range: [[1, 100]],
      },
      {
        label: "Months After The Last Appointment",
        id: "settings.reactivation.days_since_last_appointment",
        type: "select",
        required: true,
        title: "Months",
        options: Array.from({ length: 36 }, (_, i) => i + 1).map((v) => ({
          value: v * 30,
          name: v
        }))
      },
      {
        label: "Time to send",
        id: "settings.sending_date.days_since_the_event.send_time",
        type: "select",
        required: true,
        title: "Time",
        options: [
          6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13,
          13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5,
          20, 20.5, 21,
        ].map((v) => ({
          value: v,
          name: (v >= 12 && v < 13
              ? `${v} pm`
              : v >= 13
                ? `${v - 12} pm`
                : `${v} am`
          ).replace(".5", ":30"),
        })),
      },
      {
        label: "Patient Status",
        id: "settings.filter.patient_status",
        type: "radio",
        options: [
          { name: "Send to active patients", value: "active" },
          { name: "Send to inactive patients", value: "inactive" },
          { name: "Send to all patients", value: "all" },
        ],
      },
      {
        label: "Providers",
        id: "settings.filter.providers",
      },
      {
        label: "Appointment Type",
        id: "settings.filter.appointment_type",
      },
      {
        label: "Incomplete Treatments",
        id: "settings.filter.treatment_codes",
      },
      {
        label: "Oral Health Score",
        id: "settings.filter.oral_health_score_range",
        type: "rangeInt",
        range: [
          [20, 100],
          [20, 100],
        ],
      },
      preferredLanguageFilter,
    ]
  },
  {
    name: "Lead Ads",
    id: "lead-ads",
    filters: [
      {
        label: "Enabled",
        description: "",
        id: "active",
        type: "checkbox",
      },
      {
        label: "Campaign Name",
        type: "text",
        id: "name",
        required: true,
      },
      {
        label: "Facebook Campaign",
        description: "Select the Facebook campaign that should use this drip. Leave default for leads from unassigned Facebook campaigns.",
        id: "source.campaign_id",
        type: "facebook_campaign_select",
      },
      {
        label: "Drip Steps",
        description: "Define the sequence of outreach steps (calls, texts) for each lead",
        id: "settings.drip.steps",
        type: "drip_steps",
      },
      {
        label: "Goal",
        description: "Stop the sequence when this goal is reached",
        id: "settings.drip.goal.type",
        type: "select",
        options: [
          { name: "Appointment Booked", value: "appointment_booked" },
        ],
      },
      {
        label: "Stop on Contact",
        description: "Stop the drip if the patient answers (even if goal not reached)",
        id: "settings.drip.stop_on_contact",
        type: "checkbox",
      },
      {
        label: "Calling Hours Start",
        description: "Earliest time to call",
        id: "settings.drip.calling_hours.start",
        type: "select",
        defaultValue: "09:00",
        options: [
          { name: "7:00 AM", value: "07:00" },
          { name: "8:00 AM", value: "08:00" },
          { name: "9:00 AM", value: "09:00" },
          { name: "10:00 AM", value: "10:00" },
          { name: "11:00 AM", value: "11:00" },
          { name: "12:00 PM", value: "12:00" },
        ],
      },
      {
        label: "Calling Hours End",
        description: "Latest time to call",
        id: "settings.drip.calling_hours.end",
        type: "select",
        defaultValue: "17:00",
        options: [
          { name: "3:00 PM", value: "15:00" },
          { name: "4:00 PM", value: "16:00" },
          { name: "5:00 PM", value: "17:00" },
          { name: "6:00 PM", value: "18:00" },
          { name: "7:00 PM", value: "19:00" },
          { name: "8:00 PM", value: "20:00" },
        ],
      },
    ],
  }
];

export default campaignTypes;
